import { useTenantApi } from '@/account/hook/useTenantApi';
import { type TempRegistration, type TempRegistrationDeleteRequest, cartQueryKey, deleteTempRegistration, tempRegistrationsQueryKey } from '@/api/registration';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import BaseButtonPlainIconDelete from '@/base/Button/PlainIconDelete';
import BaseButtonPlainIconEdit from '@/base/Button/PlainIconEdit';
import BaseMessageBarError from '@/base/MessageBar/Error';
import {
  UiHStack,
  type UiHStackProps,
  UiSimpleGrid,
  UiStack,
  UiText,
  uiStyles
} from '@/lib/ui';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useDisclosure } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';
import RemoveConfirmation from './RemoveConfirmation';

export interface AttendeeProps extends UiHStackProps {
  attendee: TempRegistration
  groupRegistrationId: number
  onEdit: (attendee: TempRegistration) => void
}

const Attendee: FC<AttendeeProps> = ({
  attendee,
  groupRegistrationId,
  onEdit
}) => {
  const { isOpen, onToggle } = useDisclosure();
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading } = useTenantApi();
  const { userSession } = useUserSession();
  const queryClient = useQueryClient();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const { mutate: deleteMutate, isLoading: isDeleteMutationLoading } = useMutation<ApiResponseSingle<{}>, Error, TempRegistrationDeleteRequest>({
    mutationFn: async (params: TempRegistrationDeleteRequest) => {
      return await deleteTempRegistration(createTenantApiRequest)(params);
    },
    onSuccess: async (response) => {
      if (response.errors) {
        setSaveErrors(response.errors);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: [tempRegistrationsQueryKey, { eventId, groupRegistrationId }] });
      void queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }, { token: userSession?.authToken }] });
      void queryClient.invalidateQueries({ queryKey: [cartQueryKey] });
      onToggle();
      // NOTE: handle error
    },
    onError: () => {
      // NOTE: handle error
    }
  });

  const removeAttendee = (attendeeId: number) => {
    deleteMutate({ id: attendeeId });
  };

  return (
    <>
      <UiStack spacing={4} shadow={uiStyles.cardShadow} borderRadius={uiStyles.borderRadius}>
        <UiHStack px={6} py={6} bgColor={'#fff'} borderRadius={uiStyles.borderRadius} alignItems={'stretch'}>
          <UiSimpleGrid spacing={6} columns={{ base: 1, lg: 3 }} flexGrow={2}>
            <UiStack>
              <UiText color={'text.secondary'} variant={'body2'}>Email</UiText>
              <UiText>{attendee.email}</UiText>
            </UiStack>
            <UiStack>
              <UiText color={'text.secondary'} variant={'body2'}>First name</UiText>
              <UiText>{attendee.firstName}</UiText>
            </UiStack>
            <UiStack>
              <UiText color={'text.secondary'} variant={'body2'}>Last name</UiText>
              <UiText>{attendee.lastName}</UiText>
            </UiStack>
          </UiSimpleGrid>
          {/* <UiStack justifyContent={'space-between'}> */}
          {/*  <BaseButtonPlainIconDelete onClick={onToggle}/> */}
          {/*  <BaseButtonPlainIconEdit onClick={() => { return onEdit(attendee); }}/> */}
          {/* </UiStack> */}
          <UiSimpleGrid
            maxWidth={{ base: '20px', lg: '60px' }}
            minWidth={{ base: '20px', lg: '60px' }}
            columns={{ base: 1, lg: 2 }}
            gap={{ base: 6, lg: 8 }}
            alignItems={{ base: 'stretch', lg: 'flex-start' }}>
            <UiStack justifyContent={'flex-start'}>
              <UiHStack>
                <BaseButtonPlainIconDelete onClick={onToggle} />
              </UiHStack>
            </UiStack>
            <UiStack justifyContent={'flex-end'}>
              <UiHStack>
                <BaseButtonPlainIconEdit onClick={() => { return onEdit(attendee); }} />
              </UiHStack>
            </UiStack>
          </UiSimpleGrid>
          {/* <UiMenu> */}
          {/*  <UiMenuButton */}
          {/*    as={IconButton} */}
          {/*    aria-label={'Actions'} */}
          {/*    icon={<UiIconDotsThreeVertical weight={'bold'}/>} */}
          {/*    variant={'outline'} */}
          {/*    borderRadius={uiStyles.buttonRadius} */}
          {/*  /> */}
          {/*  <UiMenuList> */}
          {/*    <UiMenuItem onClick={() => { return onEdit(attendee); }}>Edit</UiMenuItem> */}
          {/*    <UiMenuItem onClick={onToggle} color={'red.500'}>Delete</UiMenuItem> */}
          {/*  </UiMenuList> */}
          {/* </UiMenu> */}
        </UiHStack>
      </UiStack>
      <RemoveConfirmation
        attendee={attendee}
        isOpen={isOpen}
        onClose={onToggle}
        onConfirm={() => { removeAttendee(attendee.id); }}
        isRemoveLoading={isDeleteMutationLoading}
      />

      {saveErrors.length > 0 && (
        <UiStack spacing={4} flexGrow={1} py={4}>
          {saveErrors.map((errors) => {
            return (
              <BaseMessageBarError key={errors} mt={3}>{errors}</BaseMessageBarError>
            );
          })}
        </UiStack>
      )}
    </>
  );
};

export default Attendee;
